import { FC, Fragment, useContext } from "react";

import { Box, Stack } from "@mui/material";
import { FormType, TicketingFormCategory } from "@simplyk/common";
import { useWatch } from "react-hook-form";

import { FormButton } from "../../../components/forms-design-system/FormButton";
import { FormTypography } from "../../../components/forms-design-system/FormTypography";
import { AmplitudeEvents } from "../../../constants/amplitude";
import { useFrontendAuctionContext } from "../../../contexts/FrontendAuctionContext";
import { FrontendTicketingContext } from "../../../contexts/FrontendTicketingContext";
import { useUserInputContext } from "../../../contexts/UserInputContext";
import { useTranslate } from "../../../hooks/useTranslate";
import { usePreviewContext } from "../../LiveFormEditor/LivePreview/context/PreviewContext";
import { GHOST_PREVIEW_OPACITY } from "../../LiveFormEditor/LivePreview/ghostPreview.constant";
import { useIsGhostPreview } from "../../LiveFormEditor/LivePreview/useIsGhostPreview";
import { useFormV2Context } from "../context/FormV2Context";
import { HeartsAnimationContext } from "../context/heartsAnimationContext";
import { useLocaleField } from "../helpers/useLocaleField";
import { useShapes } from "../sharedComponents/BackgroundShapes/ShapesContext";
import { QuestionField } from "../sharedComponents/QuestionField/QuestionField";
import { ThemedItemBlock } from "../sharedComponents/ThemedItemBlock";
import { ThemedAdditionalQuestionsForTicket } from "../sharedComponents/ThemedItemBlock/ThemedAdditionalQuestionForTicket";
import { ThemedAdditionalQuestions } from "../sharedComponents/ThemedItemBlock/ThemedAdditionalQuestions";
import { ThemedAdditionalQuestionsForGroup } from "../sharedComponents/ThemedItemBlock/ThemedAdditionalQuestionsForGroup";
import { ThemedAddItemButton } from "../sharedComponents/ThemedItemBlock/ThemedButtons/ThemedAddItemButton";
import { ThemedAddMembershipButton } from "../sharedComponents/ThemedItemBlock/ThemedButtons/ThemedAddMembershipButton";
import { ThemedItemCounterButtons } from "../sharedComponents/ThemedItemBlock/ThemedButtons/ThemedItemCounterButtons";
import { ThemedSoldOutButton } from "../sharedComponents/ThemedItemBlock/ThemedButtons/ThemedSoldOutButton";

import { useOccurrenceSelector } from "./OccurenceSelector/OccurenceSelectorContext";
import { AuctionInput } from "./TicketingSelectionV2Card/AuctionInput";
import { calculateMaximumPurchasableTicketsForRate } from "./TicketingSelectionV2Card/helpers/calculateMaximumPurchasableTicketsForRate";
import { useTicketingV2CardState } from "./TicketingSelectionV2Card/hooks/useTicketingV2CardState";

import { useAmplitude } from "@/hooks/amplitude/useAmplitude";

export const TICKETING_SELECTION_V2_RATE_ID_PREFIX = "ticketing-selection-v2-rate-";

export type BidStatus = "isOutbid" | "isWinning" | "YouDidNotBidYet";
export type PriceBidStatus = "yourBid" | "newTopBid" | "currentBid" | "startingBid";

export const TicketingSelectionV2: FC = () => {
  const { t } = useTranslate();
  const { addUserInput } = useUserInputContext();

  const {
    ticketing,
    selectedOccurrenceId,
    selectedOccurrenceWithRates,
    fullRatesReturnByOrder,
    hasADate,
    ticketingIsClosed,
    questions,
  } = useContext(FrontendTicketingContext);
  const { hasAlreadyBid, highestBidsMapByRateId, commandBidsRateIdsMap } = useFrontendAuctionContext();
  const {
    formCategory,
    validRatesSorted,
    form,
    customAnswersOfParticipantsFieldArray,
    isAuction,
    formCurrency,
    isP2PLinkedEvent,
  } = useFormV2Context(FormType.Ticketing);
  const { setOccurrenceSelectorForcedOpen } = useOccurrenceSelector();
  const getLocaleField = useLocaleField();
  const hasUnselectedOccurrence = !selectedOccurrenceId && hasADate;
  const isInteractionsDisabled = hasUnselectedOccurrence && !ticketingIsClosed; // Can interact with rate descriptions on closed ticketing
  const { rotate, reverseRotate } = useShapes();
  const { triggerHeartsAnimation } = useContext(HeartsAnimationContext);
  const { logAmplitudeEvent } = useAmplitude();

  const { isPreview, currentStepKey } = usePreviewContext();
  const isGhostPreview = useIsGhostPreview("TicketingSelectionV2");
  const isQuestionsPreviewStep = isPreview && currentStepKey === "questions";
  const isQuestionsGhostPreview = useIsGhostPreview("TicketingSelectionV2CardQuestions");

  const itemType: "ticket" | "item" | "membership" =
    formCategory === TicketingFormCategory.MembershipV2
      ? "membership"
      : formCategory === TicketingFormCategory.Shop || formCategory === TicketingFormCategory.Custom
        ? "item"
        : "ticket";

  const allRates = ticketing.rates || [];

  const { allParticipants, generateOnIncrementAmount, generateOnDecrementAmount } = useTicketingV2CardState({
    participants: customAnswersOfParticipantsFieldArray,
    form,
    rates: validRatesSorted,
    allRates,
    questions,
    animations: {
      rotate,
      reverseRotate,
      triggerHearts: triggerHeartsAnimation,
    },
    callbacks: {
      onItemAmountChanged: ({ from, to, rate }) => {
        logAmplitudeEvent(AmplitudeEvents.DonorFormTicketsCountChanged, {
          formId: ticketing.id,
          formType: ticketing.formCategory,
          organizationCountry: ticketing.organization?.country,
          ticketsCountAfterChange: from,
          ticketsCountBeforeChange: to,
          id: rate.id,
          title: rate?.rateFields?.[0]?.title,
          amount: rate.amount,
        });
      },
    },
  });

  const handleClick = () => {
    if (hasUnselectedOccurrence) {
      setOccurrenceSelectorForcedOpen(true);
    }
  };

  const firstName = useWatch({ control: form.control, name: "firstName" });

  return (
    <Stack
      sx={{
        opacity: isGhostPreview ? GHOST_PREVIEW_OPACITY : undefined,
      }}
      data-test="ticketing-selection-v2"
      direction="column"
      gap={2}
    >
      {firstName && (
        <FormTypography variant="subtitle1" vibe="intense">
          {t("ticketing", "form.welcomeMessage", { firstName })}
        </FormTypography>
      )}
      <Box onClick={handleClick}>
        <Stack
          sx={{
            pointerEvents: isInteractionsDisabled || isGhostPreview ? "none" : "auto", // Disable interactions for inner content only
            opacity: ticketingIsClosed || !hasUnselectedOccurrence || isPreview ? 1 : 0.4,
          }}
          gap={2}
        >
          {validRatesSorted?.map((rate, index) => {
            const titleAndDescription = getLocaleField(rate.rateFields);

            if (!titleAndDescription) {
              return null;
            }

            const forceFirstRatePreview = isQuestionsPreviewStep && index === 0;

            const participantsForRate = forceFirstRatePreview
              ? [{ id: "1" }, { id: "2" }] // Always show 2 tickets for the first rate in the live preview
              : allParticipants.filter((p) => p.rateId === rate.id);
            const purchasedNumber = forceFirstRatePreview
              ? 2 // Always show 2 tickets for the first rate in the live preview
              : participantsForRate.length / (rate.attendeesByTicket || 1);

            const totalParticipants = allParticipants.length;

            // Total sold tickets for the selected occurrence
            const totalSoldTickets =
              selectedOccurrenceWithRates?.rates.reduce((acc, r) => acc + (r.soldTicketsCount || 0), 0) || 0;

            const eventCapacity = ticketing?.seats || Infinity;

            // Only allow one membership per purchase or one ticket for P2P-linked event forms
            const remainingTicketingSeats =
              itemType === "membership" || isP2PLinkedEvent ? 1 : Math.max(eventCapacity - totalSoldTickets, 0);
            const currentRateByOccurrence = selectedOccurrenceWithRates?.rates.find((r) => r.rateId === rate.id);

            const {
              maximumPurchasableTicketsForRate,
              maximumPurchasableTicketsForRateAndEventCapacity,
              minRateCapacityReason: rateSoldOutReason,
            } = calculateMaximumPurchasableTicketsForRate({
              rate,
              purchasedNumber,
              totalParticipants,
              remainingTicketingSeats,
              currentRateByOccurrence,
              fullRatesReturnByOrder,
            });

            const isRateSoldOut = maximumPurchasableTicketsForRate <= 0;

            const rateQuestions = questions.filter(
              (q) => q.hasRate && q?.rateQuestions?.some((rq) => rq.rateId === rate.id)
            );

            const bidUserAmount = form.watch(`ticketsPurchased.${rate.sortIndex}.bid`) ?? 0;
            const isActive = isAuction ? bidUserAmount > 0 : purchasedNumber > 0;

            const callToAction = () => {
              if (isAuction) {
                return null;
              }

              if (ticketingIsClosed) {
                return (
                  <FormButton
                    variant="tonal"
                    sx={{
                      minWidth: { xs: 0, md: 140 },
                      pointerEvents: "none",
                      "&.ZeffyButton-root:disabled": {
                        opacity: 1, // Override the default disabled opacity as the parent component already reduces opacity when disabled
                      },
                    }}
                    disabled
                    data-test="ticket-selection-v2-card-closed-button"
                  >
                    {t("ticketing", "closed")}
                  </FormButton>
                );
              }

              if (isRateSoldOut) {
                return (
                  <ThemedSoldOutButton
                    itemType={itemType}
                    maximumToBuy={maximumPurchasableTicketsForRate}
                    maximumReason={rateSoldOutReason}
                    isP2PLinkedEvent={isP2PLinkedEvent}
                  />
                );
              }

              if (purchasedNumber > 0) {
                return (
                  <ThemedItemCounterButtons
                    itemType={itemType}
                    amount={purchasedNumber}
                    minimumToBuy={rate.minimumToBuy as number}
                    maximumToBuy={maximumPurchasableTicketsForRate}
                    maximumReason={rateSoldOutReason}
                    onIncrementClick={generateOnIncrementAmount({
                      rateId: rate.id,
                      title: titleAndDescription.title,
                    })}
                    onDecrementClick={generateOnDecrementAmount({
                      rateId: rate.id,
                      title: titleAndDescription.title,
                    })}
                  />
                );
              }

              if (itemType === "membership") {
                return (
                  <ThemedAddMembershipButton
                    onClick={generateOnIncrementAmount({ rateId: rate.id, title: titleAndDescription.title })}
                  />
                );
              }

              return (
                <ThemedAddItemButton
                  onClick={generateOnIncrementAmount({ rateId: rate.id, title: titleAndDescription.title })}
                />
              );
            };

            const additionalQuestions = () => {
              if (rateQuestions.length === 0 || !isActive) {
                return null;
              }

              if (isAuction) {
                if (hasAlreadyBid) {
                  return null;
                }

                return rateQuestions.map((question, answerIndex) => (
                  <Box key={answerIndex} marginBottom={1}>
                    <QuestionField
                      name={`customAnswersOfParticipants.${rate.sortIndex}.answers.${answerIndex}`}
                      question={question}
                      id={`rate-${index}-question-${answerIndex}`}
                    />
                  </Box>
                ));
              }

              const isGroupTicket = (rate.attendeesByTicket || 1) > 1;

              return (
                <ThemedAdditionalQuestions isGroupTicket={isGroupTicket} isPreview={isQuestionsGhostPreview}>
                  {(() => {
                    const Container = isGroupTicket ? ThemedAdditionalQuestionsForGroup : Fragment;

                    return Array.from({ length: purchasedNumber }).map((_, groupIndex) => (
                      <Container key={groupIndex}>
                        {(() => {
                          const participants = participantsForRate.slice(
                            groupIndex * (rate.attendeesByTicket || 1),
                            (groupIndex + 1) * (rate.attendeesByTicket || 1)
                          );

                          return participants.map((participant, participantIndex) => (
                            <ThemedAdditionalQuestionsForTicket
                              key={participantIndex}
                              itemType={itemType}
                              purchasedAmount={purchasedNumber}
                              groupNumber={groupIndex + 1}
                              participantNumber={participantIndex + 1}
                              isPartOfGroupTicket={isGroupTicket}
                            >
                              {rateQuestions.map((question, answerIndex) => {
                                const participantFieldIndex = allParticipants.findIndex((f) => f.id === participant.id);
                                return (
                                  <QuestionField
                                    key={answerIndex}
                                    name={`customAnswersOfParticipants.${participantFieldIndex}.answers.${answerIndex}`}
                                    question={question}
                                    id={`rate-${index}-group-${groupIndex}-participant-${participantIndex}-question-${answerIndex}`}
                                  />
                                );
                              })}
                            </ThemedAdditionalQuestionsForTicket>
                          ));
                        })()}
                      </Container>
                    ));
                  })()}
                </ThemedAdditionalQuestions>
              );
            };

            return (
              <ThemedItemBlock
                key={rate.id}
                id={`${TICKETING_SELECTION_V2_RATE_ID_PREFIX}${index}`}
                rate={rate}
                formCurrency={formCurrency}
                highestBidsMapByRateId={highestBidsMapByRateId}
                commandBidsRateIdsMap={commandBidsRateIdsMap}
                hasAlreadyBid={hasAlreadyBid}
                formCategory={formCategory}
                purchasedNumber={purchasedNumber}
                maximumPurchasableTicketsForRate={maximumPurchasableTicketsForRate}
                maximumPurchasableTicketsForRateAndEventCapacity={maximumPurchasableTicketsForRateAndEventCapacity}
                ticketingIsClosed={ticketingIsClosed}
                active={isActive}
                isSoldOut={isRateSoldOut}
                highlightEarlyBirdStatus={itemType === "membership"}
                isAuction={isAuction}
                itemType={itemType}
                isPreview={isPreview}
                questions={questions}
                form={form}
                onBidChange={(value) => addUserInput({ [titleAndDescription.title]: value })}
                amountInput={
                  isAuction && (
                    <AuctionInput
                      rate={rate}
                      indexInDisplayedRates={rate.sortIndex}
                      onBidChange={(value) => addUserInput({ [titleAndDescription.title]: value })}
                    />
                  )
                }
                indexInDisplayedRates={rate.sortIndex}
                callToAction={{ node: callToAction() }}
                additionalQuestions={{ node: additionalQuestions() }}
              />
            );
          })}
        </Stack>
      </Box>
    </Stack>
  );
};
